/**
 * ConceptMappingsTable — read-only "Concept mappings (N)" section for the AV
 * Definition tab (OR-6214). Renders the structured term→concept mappings the
 * agent produced (OR-6018): a two-column Term / Mapped-to table where each
 * row's "Mapped to" cell shows curated concept-group chips, inline
 * standard-OMOP concept chips (``name (id)``), or both together when a
 * mapping combines a curated group with ad-hoc extra concepts (OR-6424).
 *
 * Read-only: no add/remove/edit. Clicking a group chip opens that concept
 * group's details; clicking an inline-concept chip opens the OMOP concept
 * details (resolved by id). A term's chips are stacked vertically and all shown;
 * the whole table scrolls within a max height when there are many mappings. Long
 * chip labels ellipsis-truncate (full name on hover) so they never blow out the
 * cell.
 *
 * Layout + type follow Figma node 428-20887:
 *   - section title: body1 / semibold
 *   - column headers ("Term" / "Mapped to"): table/header (body2 medium, 0.17)
 *   - term values: body2
 *   - the table body is capped and scrolls vertically.
 *
 * The section renders only when there is at least one mapping.
 */

import { Box, Chip, Stack, Typography } from '@mui/material';
import { type ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';

import { ConceptGroupDetailsDialog } from '@verantos-dev/feature-concept-groups-list';
import { ConceptGroupChip } from '@verantos-dev/shared-components';

import { OmopConceptDetailsDialog } from './OmopConceptDetailsDialog';
import { ResolvedConceptGroupChip } from './ResolvedConceptGroupChip';
import { useBatchPrewarmConceptGroups } from '../../hooks/useBatchPrewarmConceptGroups';
import type { ConceptMapping, ConceptMappingGroup } from '../../types/epi-chat-types';

interface ConceptMappingsTableProps {
  mappings: ConceptMapping[];
  /** Optional dataset release context for the concept-group details prevalence panel. */
  datasetReleaseId?: string | null;
  datasetReleaseDisplayName?: string | null;
  /**
   * Explanatory line between the heading and the table. Used by the variable
   * GROUP surface (OR-6504), where the mapping pool is shared across N variables
   * and that needs saying; absent for a single variable, whose mappings are its
   * own.
   */
  caption?: ReactNode;
  /**
   * A third column, appended after "Mapped to". The variable GROUP surface uses
   * it for "Applied to" — which of the numbered variables reference each term —
   * a question that only exists when one pool serves several variables.
   *
   * Passed as a render prop rather than a field on ``ConceptMapping`` so this
   * table stays ignorant of families: the caller closes over whatever it needs.
   */
  extraColumn?: {
    header: string;
    /** Column width, e.g. ``'18%'``. Defaults to sharing the "Mapped to" space. */
    width?: string;
    render: (mapping: ConceptMapping) => ReactNode;
  };
}

/** Term column share of the table width (Figma 406/853 ≈ 47%). */
const TERM_COL_WIDTH = '46%';

/** Width of the optional third column (OR-6504's "Applied to"). */
const EXTRA_COL_WIDTH = '18%';

/** Max height of the scrollable body before it scrolls (Figma fixed body ≈ 571px). */
const BODY_MAX_HEIGHT = 571;

/**
 * Finding #20: gate the by-id name override so it only fires for a *broken*
 * binding — a stored ``name`` that is missing or is itself an id (the backend
 * fell back to the id as the name). A good manifest name (resolved at the source
 * by backend WS-A and shown identically in the chat link / replay) is TRUSTED
 * and never second-guessed against the live catalog, so the two surfaces can't
 * disagree. This replaces the old unconditional override.
 */
const isIdLikeOrMissingName = (g: ConceptMappingGroup): boolean => {
  const name = g.name?.trim();
  return !name || name === g.conceptGroupId;
};

// table/header token: Roboto Medium 14, lineHeight 24, letterSpacing 0.17.
const headerCellSx = {
  display: 'flex',
  alignItems: 'center',
  height: 56,
  paddingX: 2,
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '0.17px',
  color: 'text.primary',
} as const;

const termCellSx = {
  width: TERM_COL_WIDTH,
  flexShrink: 0,
  paddingX: 2,
  paddingY: 2,
  color: 'text.primary',
} as const;

const mappedCellSx = {
  flex: 1,
  minWidth: 0,
  paddingX: 2,
  paddingY: 2,
} as const;

export const ConceptMappingsTable = ({
  mappings,
  datasetReleaseId = null,
  datasetReleaseDisplayName = null,
  caption,
  extraColumn,
}: ConceptMappingsTableProps) => {
  const { formatMessage } = useIntl();
  const [openConceptGroupId, setOpenConceptGroupId] = useState<string | null>(null);
  const [openInlineConcept, setOpenInlineConcept] = useState<{ id: number; name: string } | null>(
    null
  );

  // OR-6858: every group this table will resolve by id (mirrors the `resolveById`
  // condition below) — batched into one request instead of one per chip.
  const idsToResolve = mappings.flatMap((mapping) =>
    mapping.conceptGroups
      .filter((g) => !!g.conceptGroupId && (isIdLikeOrMissingName(g) || !g.owner))
      .map((g) => g.conceptGroupId)
  );
  const deferConceptGroupFetch = useBatchPrewarmConceptGroups(idsToResolve);

  if (mappings.length === 0) return null;

  return (
    <Box data-testid="artifact-concept-mappings-section">
      <Typography variant="body1" sx={{ fontWeight: 600, marginBottom: caption ? 0.5 : 2 }}>
        {formatMessage({ id: 'epiChat.conceptMappings.heading' }, { count: mappings.length })}
      </Typography>
      {caption && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="p"
          sx={{ marginBottom: 2 }}
          data-testid="concept-mappings-caption"
        >
          {caption}
        </Typography>
      )}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 1,
          overflow: 'hidden',
          backgroundColor: 'background.paper',
          boxShadow:
            '0px 1px 3px 0px rgba(0,0,0,0.12), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
          data-testid="concept-mappings-header"
        >
          <Box sx={{ ...headerCellSx, width: TERM_COL_WIDTH, flexShrink: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, letterSpacing: '0.17px' }}>
              {formatMessage({ id: 'epiChat.conceptMappings.column.term' })}
            </Typography>
          </Box>
          <Box sx={{ ...headerCellSx, flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, letterSpacing: '0.17px' }}>
              {formatMessage({ id: 'epiChat.conceptMappings.column.mappedTo' })}
            </Typography>
          </Box>
          {extraColumn && (
            <Box
              sx={{
                ...headerCellSx,
                width: extraColumn.width ?? EXTRA_COL_WIDTH,
                flexShrink: 0,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, letterSpacing: '0.17px' }}>
                {extraColumn.header}
              </Typography>
            </Box>
          )}
        </Stack>
        {/* Body — scrolls within a max height when there are many mappings. */}
        <Box sx={{ maxHeight: BODY_MAX_HEIGHT, overflowY: 'auto' }}>
          {mappings.map((mapping, idx) => {
            // OR-6424: a mapping's concept groups and inline concepts are two
            // independent facts, not a choice — render whichever are
            // non-empty, both together when both are (a curated group plus
            // ad-hoc extras).
            const hasGroups = mapping.conceptGroups.length > 0;
            const hasInline = mapping.inlineConcepts.length > 0;
            const hasConcepts = hasGroups || hasInline;

            return (
              <Stack
                key={mapping.mappingId}
                direction="row"
                sx={{
                  borderBottom: idx < mappings.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  alignItems: 'flex-start',
                }}
                data-testid="concept-mappings-row"
              >
                <Box sx={termCellSx}>
                  <Typography variant="body2">{mapping.clinicalTerm}</Typography>
                </Box>
                <Box sx={mappedCellSx}>
                  {!hasConcepts ? (
                    <Typography variant="body2" color="text.secondary">
                      {formatMessage({ id: 'epiChat.conceptMappings.empty' })}
                    </Typography>
                  ) : (
                    <Stack direction="column" gap={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
                      {hasGroups &&
                        mapping.conceptGroups.map((g, groupIdx) => {
                          // Resolve the group by id via the catalog when the
                          // manifest row is missing something the chip needs:
                          //  - Bug 7 / finding #20: a BROKEN stored ``name``
                          //    (missing or itself an id) → resolve the name too.
                          //  - OR-6335: a good name but a missing ``owner`` →
                          //    resolve OWNER ONLY (the chip icon distinguishes a
                          //    Verantos group from a custom one, and older
                          //    manifests persisted no owner). The trusted name
                          //    is kept; only the icon fills in from the catalog.
                          // Both need a real id.
                          const nameBroken = isIdLikeOrMissingName(g);
                          const ownerMissing = !g.owner;
                          const resolveById = !!g.conceptGroupId && (nameBroken || ownerMissing);
                          return resolveById ? (
                            <ResolvedConceptGroupChip
                              key={g.conceptGroupId}
                              conceptGroupId={g.conceptGroupId}
                              fallbackOwner={g.owner ?? undefined}
                              // Broken name → let the id drive the name too;
                              // good name → trust it and resolve owner only.
                              displayName={nameBroken ? undefined : g.name}
                              onOpen={setOpenConceptGroupId}
                              deferFetch={deferConceptGroupFetch}
                              maxWidth="100%"
                            />
                          ) : (
                            <ConceptGroupChip
                              // Dup-key guard: id-less groups in the same row
                              // disambiguate on the inner group index.
                              key={g.conceptGroupId || `${g.name}-${groupIdx}`}
                              name={g.name}
                              owner={g.owner ?? undefined}
                              onClick={
                                g.conceptGroupId
                                  ? () => setOpenConceptGroupId(g.conceptGroupId)
                                  : undefined
                              }
                              maxWidth="100%"
                            />
                          );
                        })}
                      {hasInline &&
                        mapping.inlineConcepts.map((c) => (
                          <Chip
                            key={c.conceptId}
                            label={`${c.name} (${c.conceptId})`}
                            size="small"
                            variant="outlined"
                            onClick={() => setOpenInlineConcept({ id: c.conceptId, name: c.name })}
                            sx={{ maxWidth: '100%', minWidth: 0 }}
                            data-testid="concept-mapping-inline-chip"
                          />
                        ))}
                    </Stack>
                  )}
                </Box>
                {extraColumn && (
                  <Box
                    sx={{
                      ...mappedCellSx,
                      flex: 'none',
                      width: extraColumn.width ?? EXTRA_COL_WIDTH,
                      color: 'text.primary',
                    }}
                    data-testid="concept-mappings-extra-cell"
                  >
                    {extraColumn.render(mapping)}
                  </Box>
                )}
              </Stack>
            );
          })}
        </Box>
      </Box>

      {openConceptGroupId && (
        <ConceptGroupDetailsDialog
          conceptGroupId={openConceptGroupId}
          datasetReleaseId={datasetReleaseId}
          datasetReleaseDisplayName={datasetReleaseDisplayName}
          onClose={() => setOpenConceptGroupId(null)}
        />
      )}
      {openInlineConcept && (
        <OmopConceptDetailsDialog
          conceptId={openInlineConcept.id}
          fallbackName={openInlineConcept.name}
          onClose={() => setOpenInlineConcept(null)}
        />
      )}
    </Box>
  );
};
